import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useReducer,
} from 'react';
import moment from 'moment-timezone';
import produce from 'immer';
import {
  mapDoctors,
  mapCertDoctorsWithPecos,
} from '@mummssoftware/utils/utils';
import { getPhysicians } from '@mummssoftware/utils/pimReader';
import { cert } from '@mummssoftware/utils/constants';
import {
  ATTENDING_FORM,
  CERTIFYING_FORM,
  RESET_VALUES,
  SET_DOCTORS,
  SET_CERT_DOCTORS,
  SET_DOCTOR,
  SET_DATE,
  SET_FORM_TYPE,
  SET_PICKER_STATE,
  SET_PIM,
  SET_CLEARSCRIPTS,
  SET_WORKFLOW,
  SET_OBSERVATION,
  SET_CERT_START_DATE,
  SET_VERBAL_DATE_LIMIT,
  SET_LATE_CERT,
  SET_EMBEDDED,
  SET_DIMENSIONS,
  SET_VERBAL_DATE,
  SET_BUTTON_CLICKED,
  SET_SMALL_WIDGET,
  SET_SELECTED_PATIENT,
} from './constants';

const initCertContext = {
  pim: null,
  setDoctors: null,
  fetchCertDoctorsWithDate: null,
  resetVerbal: null,
  formType: CERTIFYING_FORM,
  doctors: { [ATTENDING_FORM]: [], [CERTIFYING_FORM]: [] },
};

const defaultDoctor = null;

const createInitialState = (
  pim,
  clearScripts,
  workflow,
  observation,
  embedded,
  smallWidget,
  agency,
  patient,
  keycloak,
) => ({
  pim,
  clearScripts,
  workflow,
  observation,
  embedded,
  smallWidget,
  agency,
  patient,
  keycloak,
  formType: CERTIFYING_FORM,
  doctors: { [ATTENDING_FORM]: [], [CERTIFYING_FORM]: [] },
  selectedDoctor: null,
  selectedDate: null,
  pickerDisabled: false,
  certStartDate: null,
  verbalDateLimit: null,
  lateCert: false,
  lateCertConfirmed: true,
  dimensions: null,
  verbalDate: null,
  buttonClicked: null,
  selectedPatient: null,
  siteDoctors: null,
});

const CertContextReducer = (draft, action) => {
  switch (action.type) {
    case RESET_VALUES:
      // @note: picker is disabled when we have a programmatically set doctor.
      if (!draft.pickerDisabled) {
        draft.selectedDoctor = defaultDoctor;
      }
      break;
    case SET_DOCTORS:
      draft.selectedDoctor = defaultDoctor;
      draft.doctors = action.payload;
      break;
    case SET_CERT_DOCTORS:
      draft.selectedDoctor = defaultDoctor;
      draft.doctors[CERTIFYING_FORM] = action.payload;
      break;
    case SET_DOCTOR:
      draft.pickerDisabled = action.disablePicker;
      draft.selectedDoctor = action.payload;
      break;
    case SET_DATE:
      draft.selectedDate = action.payload;
      break;
    case SET_FORM_TYPE:
      draft.formType = action.payload;
      break;
    case SET_PICKER_STATE:
      draft.pickerDisabled = action.dispatch;
      break;
    case SET_PIM:
      draft.pim = action.payload;
      break;
    case SET_CLEARSCRIPTS:
      draft.clearScripts = action.payload;
      break;
    case SET_WORKFLOW:
      draft.workflow = action.payload;
      break;
    case SET_OBSERVATION:
      draft.observation = action.payload;
      break;
    case SET_CERT_START_DATE:
      draft.certStartDate = action.payload;
      break;
    case SET_VERBAL_DATE_LIMIT:
      draft.verbalDateLimit = action.payload;
      break;
    case SET_LATE_CERT:
      draft.lateCert = action.payload;
      break;
    case SET_EMBEDDED:
      draft.embedded = action.payload;
      break;
    case SET_SMALL_WIDGET:
      draft.smallWidget = action.payload;
      break;
    case SET_DIMENSIONS:
      draft.dimensions = action.payload;
      break;
    case SET_VERBAL_DATE:
      draft.verbalDate = action.payload;
      break;
    case SET_BUTTON_CLICKED:
      draft.buttonClicked = action.payload;
      break;
    case SET_SELECTED_PATIENT:
      draft.selectedPatient = action.payload;
      break;
    default:
  }
};

const curriedContextReducer = produce(CertContextReducer);

export const CertContext = createContext(initCertContext);
export const CertDispatchContext = createContext();
export const CertContextConsumer = CertContext.Consumer;
export const CertDispatchContextConsumer = CertDispatchContext.Consumer;

/**
 *
 * @param {{ children: React.ReactChildren, pim?: import('@mummssoftware/utils').Pim }} props
 */
export const CertContextProvider = ({
  children,
  pim,
  clearScripts,
  workflow,
  observation,
  embedded,
  smallWidget,
  agency,
  patient,
  keycloak,
}) => {
  const isMounted = useRef(false);
  const [certState, dispatch] = useReducer(
    curriedContextReducer,
    createInitialState(
      pim,
      clearScripts,
      workflow,
      observation,
      embedded,
      smallWidget,
      agency,
      patient,
      keycloak,
    ),
  );

  const resetVerbal = useCallback(() => {
    dispatch({ type: SET_VERBAL_DATE, payload: null });
  }, []);

  const setDoctors = useCallback(
    (site, agencyOverride) => {
      const agencyForRequest = agencyOverride ?? agency;

      async function fetchDoctors(siteId) {
        if (pim) {
          const doctors = await pim.getPhysicianRoles(siteId, agencyForRequest);
          if (isMounted.current) {
            const certDoctors = await getPhysicians(
              keycloak,
              agencyForRequest,
              patient,
              cert,
            );

            dispatch({
              type: SET_DOCTORS,
              payload: {
                [ATTENDING_FORM]: mapDoctors(doctors),
                [CERTIFYING_FORM]: mapDoctors(certDoctors),
              },
            });
          }
        }
      }
      fetchDoctors(site);
    },
    [pim, agency, keycloak, patient],
  );
  const fetchCertDoctorsWithDate = useCallback(
    (date, patientOverride, agencyOverride) => {
      const agencyForRequest = agencyOverride ?? agency;

      async function fetchCertDoctors() {
        const targetPatient =
          patientOverride || certState.selectedPatient || patient;
        const patientNumber =
          targetPatient?.patientNumber ?? targetPatient?.patientId;
        if (!keycloak || !agencyForRequest || !patientNumber || !date) return;
        const dateStr = moment(date).format('YYYY-MM-DD');
        if (!isMounted.current) return;
        const certDoctors = await getPhysicians(
          keycloak,
          agencyForRequest,
          { patientNumber },
          cert,
          dateStr,
        );
        if (!isMounted.current) return;
        dispatch({
          type: SET_CERT_DOCTORS,
          payload: mapCertDoctorsWithPecos(certDoctors || []),
        });
      }
      fetchCertDoctors();
    },
    [keycloak, agency, patient],
  );

  useEffect(() => {
    if (pim !== certState.pim) {
      dispatch({ type: SET_PIM, payload: pim });
    }
  }, [certState.pim, pim]);

  useEffect(() => {
    if (clearScripts !== certState.clearScripts) {
      dispatch({ type: SET_CLEARSCRIPTS, payload: clearScripts });
    }
  }, [certState.clearScripts, clearScripts]);

  useEffect(() => {
    if (workflow !== certState.workflow) {
      dispatch({ type: SET_WORKFLOW, payload: workflow });
    }
  }, [certState.workflow, workflow]);

  useEffect(() => {
    if (observation !== certState.observation) {
      dispatch({ type: SET_OBSERVATION, payload: observation });
    }
  }, [certState.observation, observation]);

  useEffect(() => {
    if (embedded !== certState.embedded) {
      dispatch({ type: SET_EMBEDDED, payload: embedded });
    }
  }, [certState.embedded, embedded]);

  useEffect(() => {
    if (smallWidget !== certState.smallWidget) {
      dispatch({ type: SET_SMALL_WIDGET, payload: smallWidget });
    }
  }, [certState.smallWidget, smallWidget]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);
  const DispatchValue = useMemo(
    () => ({ dispatch, setDoctors, fetchCertDoctorsWithDate, resetVerbal }),
    [dispatch, setDoctors, fetchCertDoctorsWithDate, resetVerbal],
  );
  return (
    <CertContext.Provider value={certState}>
      <CertDispatchContext.Provider value={DispatchValue}>
        {children}
      </CertDispatchContext.Provider>
    </CertContext.Provider>
  );
};

export function withCertContext(Component) {
  return function contextComponent(props) {
    return (
      <CertContextConsumer>
        {(context) => (
          <CertDispatchContextConsumer>
            {(dispatchContext) => (
              <Component {...props} {...context} {...dispatchContext} />
            )}
          </CertDispatchContextConsumer>
        )}
      </CertContextConsumer>
    );
  };
}
